import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Box, Typography } from "@mui/material";

export const getSalesHeader = (data = [], sectionName) => {
  if (!data || data.length === 0) return [];

  // First 3 fixed columns
  const fixedCols = [
    {
      label: "Scope",
      id: "scope",
      width: 200,
      freeze: true,
    },
     {
      label: "Particular",
      id: "particular",
      width: 160,
      freeze: true,

      render: (row) => {
        return (
          <Box
            sx={{
              display: "flex",
              minWidth: "130px",
            }}
          >
            <span>{row?.particular}</span>
          </Box>
        );
      },
    },    
    {
      label: "Metric",
      id: "metric",
      width: 200,
      freeze: true,

      render: (row) => {
        return (
          <Box
            sx={{
              display: "flex",
              minWidth: "200px",
            }}
          >
            <span>{row?.metric}</span>
          </Box>
        );
      },
    },
    {
      label: "Cohort",
      id: "cohort",
      width: 550,
      freeze: true,
      render: (row) => {
        return (
          <Box
            sx={{
              display: "flex",
              minWidth: "250px",
            }}
          >
            <span>{row?.cohort}</span>
          </Box>
        );
      },
    },
  ];

  const groupColors = {
    MTD: "#f36440",
    QTD: "#e6e41a",
    YTD: "#c575f1",
    Default: "#f9f9f9",
  };

  const firstRow = data[0];
  const groups = {};

  ["MTD", "QTD", "YTD"].forEach((group) => {
    if (firstRow[group] && Array.isArray(firstRow[group])) {
      groups[group] = firstRow[group].map((item, index) => ({
        label: item.key,
        id: `${group}-${item.key}`, // matches flattened row keys
        render: (row) => {
          const value = row[`${group}-${item.key}`] ?? "-";
          const isComparison = item.key.startsWith("v/s");
          const n = parseFloat((value || "0").toString().replace("%", "")) || 0;
          const getIcon = () => {
            if (n === 0) {
              return (
                <ArrowRightIcon
                  sx={{ color: "#e3e5e8", margin: "-10px 0px -10px -10px;" }}
                  fontSize="large"
                />
              );
            }
            if (n > 0) {
              return (
                <ArrowDropUpIcon
                  sx={{ color: "#4caf50", margin: "-10px 0px -10px -10px;" }}
                  fontSize="large"
                />
              ); // green up
            }
            if (n < 0 && n >= -5) {
              return (
                <ArrowRightIcon
                  sx={{ color: "#fbc02d", margin: "-10px 0px -10px -10px;" }}
                  fontSize="large"
                />
              ); // yellow right
            }
            return (
              <ArrowDropDownIcon
                sx={{ color: "#f44336", margin: "-10px 0px -10px -10px;" }}
                fontSize="large"
              />
            ); // red down
          };
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: item?.key?.includes("v/s")
                  ? "space-between"
                  : "flex-end",
                minWidth: "130px",
              }}
            >
              {item?.key?.includes("v/s") ? getIcon() : ""}
              <span
                style={{
                  color: isComparison ? "blue" : "inherit",
                }}
              >
                {value}
              </span>
            </Box>
          );
        },
      }));
    }
  });

  const dynamicCols = Object.keys(groups).map((group) => {
    return {
      label: group,
      id: group,
      background: groupColors[group] || groupColors.Default,
      columns: groups[group].map((col, index) => ({
        ...col,
        headerBackground: col.label.includes("v/s")
          ? "#fff5b9"
          : index === 0 || index === 1
          ? "#bbbdbb"
          : index === 2
          ? "#b5e6a2"
          : "#bdd7ee",
      })),
    };
  });

  return [...fixedCols, ...dynamicCols];
};

// helper.js
export const getTrendsHeader = (data = [], sectionName) => {
  if (!data || data.length === 0) return [];

  // First 4 fixed columns
  const fixedCols = [
    { label: "Scope", id: "scope", width: 200, freeze: true },
    {
      label: "Particular",
      id: "particular",
      width: 160,
      freeze: true,

      render: (row) => {
        return (
          <Box
            sx={{
              display: "flex",
              minWidth: "130px",
            }}
          >
            <span>{row?.particular}</span>
          </Box>
        );
      },
    },
    {
      label: "Metric",
      id: "metric",
      width: 160,
      freeze: true,
      render: (row) => {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              minWidth: "130px",
            }}
          >
            <span>{row?.metric}</span>
          </Box>
        );
      },
    },
    {
      label: "Cohort",
      id: "cohort",
      width: 550,
      freeze: true,
      render: (row) => {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              minWidth: "130px",
            }}
          >
            <span>{row?.cohort}</span>
          </Box>
        );
      },
    },
  ];

  const firstRow = data[0];
  const dynamicCols = [];

  if (firstRow.data && Array.isArray(firstRow.data)) {
    firstRow.data.forEach((item) => {
      dynamicCols.push({
        label: item.key,
        id: item.key,
        background: "#ffe0b2",
        render: (row) => {
          const value = row[item.key] ?? "-";
          const isComparison = item.key.startsWith("v/s");
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                minWidth: "130px",
              }}
            >
              <span style={{ color: isComparison ? "blue" : "inherit" }}>
                {value}
              </span>
            </Box>
          );
        },
      });
    });
  }

  return [...fixedCols, ...dynamicCols];
};

export const COHORT_KEYS = [
  "Overall Company",
  "Overall Company LFL",
  "Overall Company LFL non-split",
  "FOFO",
  "FOFO excl. airport",
  "FOFO LFL",
  "FOFO LFL non-split",
  "COCO",
  "COCO LFL",
  "COCO LFL non-split",
  "COCO Cat-1 / FY23",
  "COCO Cat-2 / FY24",
  "COCO Cat-3 / FY25",
  "COCO Cat-3 / FY25 wo Amex",
  "COCO Cat-4 / FY26",
  "COCO Built",
  "COCO Built LFL",
  "COCO Built LFL non-split",
  "COCO Built Cat-1 / FY23",
  "COCO Built Cat-2 / FY24",
  "COCO Built Cat-3 / FY25",
  "COCO Built Cat-3 / FY25 wo Amex",
  "COCO Built Cat-4 / FY26",
  "COCO Acquired",
  "COCO Acquired LFL",
  "COCO Acquired LFL non-split",
  "COCO Acquired Cat-1 / FY23",
  "COCO Acquired Cat-2 / FY24",
  "COCO Acquired Cat-3 / FY25",
  "COCO Acquired Cat-4 / FY26",
];
export const METRIC_KEYS = [
  "Store Count (#)",
  "ADS (INR)",
  "Dine-in ADS (INR)",
  "Delivery ADS (INR)",
  "ADT (bills / day)",
  "Dine-in ADT (bills / day)",
  "Delivery ADT (bills / day)",
  "APC (INR)",
  "Dine-in APC (INR)",
  "Delivery APC (INR)",
  "Store Days (#)",
  "Revenue (INR)",
  "Dine-in Revenue (INR)",
  "Delivery Revenue (INR)",
  "Traffic (#)",
  "Dine-in Traffic (#)",
  "Delivery Traffic (#)",
  "Delivery%",
];
